/**
 * The dark "why create an account" panel beside the registration form on `/register`
 * (`Register.tsx`). It has no form or action, despite its name: it lists the account benefits
 * and, on the Ryerson build, the stats blurb. All copy comes from the login page's
 * `components/Login/LoginHelpers/LoginContent.tsx` → `LoginContent`, shared with the login page's
 * own create-account panel, `components/Login/CreateAccountForm.tsx`.
 */
import React from "react";
import { css } from "@emotion/react";
import { useTheme } from "@ryerson/frontend.theme";
import { Flex, FlexItem } from "@ryerson/frontend.layout";
import { Icon } from "@ryerson/frontend.assets";
import { Typography } from "@ryerson/frontend.typography";
import styled from "@emotion/styled";
import { useApplication } from "@ryerson/frontend.application";
import { LoginContent } from "@components/Login/LoginHelpers/LoginContent";

/** `isMobile` selects the stacked-layout padding and margins. */
export interface AccountProps {
	className?: string;
	isMobile: boolean;
}

const checkIcon = css`
	margin-right: 20px;
	align-self: center;
`;

const Divider = styled.hr`
	width: 100%;
	margin-bottom: 45px;
	${(props: any) => {
		const { theme } = props;
		return css`
			color: ${theme.colors.primary.darkerGrey};
			opacity: 0.1;
		`;
	}}
`;

/** The account-benefits panel; see the file header. */
const AccountForm: React.FC<AccountProps> = ({ className = "", isMobile }) => {
	const { theme } = useTheme();
	const { white } = theme.colors.primary;

	const {
		localization: { language },
	} = useApplication();

	const company = process.env.NEXT_PUBLIC_COMPANY;

	const content = LoginContent[language] || LoginContent.en;

	return (
		<Flex
			className={className}
			direction="column"
			justifyContent="flex-start"
			css={{
				width: "100%",
				padding: isMobile ? "25px 0px 30px 0px" : "40px 35px 35px 35px",
			}}
		>
			<Typography
				size="lg"
				css={css`
					margin-bottom: 45px;
				`}
				color={white}
			>
				{content.createAccountText}
			</Typography>
			<Divider {...{ theme }} />
			<FlexItem
				style={css`
					margin-bottom: ${isMobile ? "40px" : "56px"};
					margin-top: ${isMobile ? "0px" : "50px"};
				`}
			>
				<Flex css={{ marginBottom: "12px" }}>
					<Icon icon="check" color={white} size="xs" css={checkIcon} />
					<Typography variant="div" color={white} css={{ marginTop: "0px" }}>
						{content.accessText}
					</Typography>
				</Flex>
				<Flex css={{ marginBottom: "12px" }}>
					<Icon icon="check" color={white} size="xs" css={checkIcon} />
					<Typography variant="div" color={white} css={{ marginTop: "0px" }}>
						{content.saveToShoppingList}
					</Typography>
				</Flex>

				<Flex css={{ marginBottom: "12px" }}>
					<Icon icon="check" color={white} size="xs" css={checkIcon} />
					<Typography variant="div" color={white} css={{ marginTop: "0px" }}>
						{content.quotesText}
					</Typography>
				</Flex>
				<Flex css={{ marginBottom: "12px" }}>
					<Icon icon="check" color={white} size="xs" css={checkIcon} />
					<Typography variant="div" color={white} css={{ marginTop: "0px" }}>
						{content.payInvoiceText}
					</Typography>
				</Flex>
				<Flex css={{ marginBottom: "12px" }}>
					<Icon icon="check" color={white} size="xs" css={checkIcon} />
					<Typography variant="div" color={white} css={{ marginTop: "0px" }}>
						{content.fasterCheckout}
					</Typography>
				</Flex>
				<Flex css={{ marginBottom: "12px" }}>
					<Icon icon="check" color={white} size="xs" css={checkIcon} />
					<Typography variant="div" color={white} css={{ marginTop: "0px" }}>
						{content.orderRenewalText}
					</Typography>
				</Flex>
			</FlexItem>
			<Divider {...{ theme }} />
			{/* Ryerson shows the stats title with its figures and captions; any other brand shows
			    only the title. */}
			{company === "ryerson" ? (
				<>
					<FlexItem>
						<Typography color={white} weight="bold" size="md">
							{content.statsBlurb?.title}
						</Typography>
					</FlexItem>
					<FlexItem
						css={css`
							height: 10px;
							margin-top: 10px;
						`}
					>
						<Flex direction="column">
							<FlexItem>
								<Flex>
									{content.statsBlurb?.statBlurbText.map(
										(string: string, index: number) => (
											<React.Fragment key={index}>
												<Typography
													variant={"h2"}
													type="tertiary"
													css={css`
														margin-top: 0px;
														margin-right: 38px;
														margin-bottom: 20px;
													`}
												>
													{string}
												</Typography>
											</React.Fragment>
										)
									)}
								</Flex>
							</FlexItem>
							<FlexItem>
								<Flex>
									{content.statsBlurb?.statSubBlurb.map(
										(string: string, index: number) => (
											<React.Fragment key={index}>
												<br />
												<Typography
													type="tertiary"
													css={css`
														margin-top: 0px;
														margin-right: 145px;
													`}
													size="md"
												>
													{string}
												</Typography>
											</React.Fragment>
										)
									)}
								</Flex>
							</FlexItem>
							<FlexItem>
								<Flex>
									{content.statsBlurb?.statSubBlurb2.map(
										(string: string, index: number) => (
											<React.Fragment key={index}>
												<Typography
													type="tertiary"
													css={{
														marginTop: isMobile ? "10px" : "0px",
														marginRight: "123px",
													}}
													size="md"
												>
													{string}
												</Typography>
											</React.Fragment>
										)
									)}
								</Flex>
							</FlexItem>
						</Flex>
					</FlexItem>
				</>
			) : (
				<FlexItem>
					<Typography color={white} css={{ marginTop: "0px" }} weight="bold" size="md">
						{content.statsBlurb?.title}
					</Typography>
				</FlexItem>
			)}
		</Flex>
	);
};

export default AccountForm;
